import { has, get } from "lodash";
import { stringify } from "query-string";
import { fetchUtils, Identifier, Record, DeleteResult } from "react-admin";
import axios from "axios";
import capitalize from "lodash/capitalize";

interface Query {
  pageNumber?: number;
  pageSize?: number;
  OrderType?: string;
  OrderBy?: string;
  filter?: any;
  Search?: string;
}

export const composeError = (errorBody: any) => {
  const errors: string[] = Object.values(get(errorBody, "errors", {}));
  const message = get(errorBody, "message", []);
  let msg = `${message}. \n`;
  errors.map((val: string) => (msg = msg + val + "\n"));
  return msg;
};

const dataProvider = {
  getList: (resource: any, params: any) => {
    let query: Query = {};

    if (has(params, "pagination")) {
      const { page, perPage } = params.pagination;
      query["pageNumber"] = page;
      query["pageSize"] = perPage;
    }

    if (has(params, "sort")) {
      const { field, order } = params.sort;
      let splittedString = field.split(".");
      if (splittedString.length !== 0) {
        const capitalizedString = splittedString.map((text: string) => {
          return text.charAt(0).toUpperCase() + text.slice(1);
        });

        query["OrderBy"] = capitalizedString.join(".");
      } else {
        query["OrderBy"] = field.charAt(0).toUpperCase() + field.slice(1);
      }
      query["OrderType"] = order.toLowerCase();
    }

    if (has(params, "filter")) {
      // if (params.filter.SchoolId === 0) {
      //   params.filter.SchoolId = null;
      // }

      if (params.filter.search) {
        query["Search"] = params.filter.search ?? "";
        query["filter"] = JSON.stringify(params.filter);

        if (resource === "parents" && params.filter.searchAllParent) {
          delete params.filter.SchoolId;
          delete params.filter.IsHaveStudent;
        }
      } else {
        query["filter"] = JSON.stringify(params.filter);
      }
    }

    let url = `${apiUrl}/${resource}?${stringify(query)}`;

    return httpClient(url, getUser())
      .then(({ json, headers }: any) => {
        // if it has json.meta, then spread the meta
        const jsons = {
          data: json,
        };
        const metas = {
          total: jsons.data.length,
        };
        const meta = get(jsons, "meta");
        const ret = {
          json: {
            ...jsons,
            ...JSON.parse(headers.get("X-Pagination")),
          },
        };
        return ret;
      })
      .then(({ json }: any) => ({
        data: json.data,
        total: json.TotalCount,
      }));
  },

  getOne: async (resource: any, params: any) => {
    console.log("RESOURCE_GET_ONE", resource);
    const { json } = await httpClient(
      `${apiUrl}/${resource}/${params.id}`,
      getUser()
    );
    if (resource === "tenantsettings") {
      if (json.key === "schedule_pdf") {
        // Manipulate
        const documents = JSON.parse(json.value);
        json.documents = documents.map((document: any) => ({
          title: document.Title,
          file: {
            title: document.Url,
            url: document.Url,
          },
        }));
      }
      if (json.key === "mail_list") {
        // Manipulate
        const emails = JSON.parse(json.value);
        json.emails = emails.map((o: any) => ({
          value: o,
        }));
      }
    }
    return "data" in json ? json : { data: json };
  },

  getMany: (resource: any, params: any) => {
    console.log("GET MANY", params.ids);
    let result = null;
    if (typeof params.ids[0] === "object") {
      result = params.ids.map((obj: any) => obj.id);
    } else {
      result = params.ids;
    }
    const query = {
      ids: result.toString(),
    };
    const url = `${apiUrl}/${resource}?${stringify(query)}`;
    return httpClient(url, getUser()).then(({ json }: any) => ({
      data: json,
    }));
  },

  getManyReference: (resource: any, params: any) => {
    let query: Query = {};

    if (params.pagination) {
      const { page, perPage } = params.pagination;
      query["pageNumber"] = page;
      query["pageSize"] = perPage;
    }

    if (params.sort) {
      const { field, order } = params.sort;
      query["OrderBy"] = capitalize(field);
      query["OrderType"] = order.toLowerCase();
    }

    if (params.filter) {
      query["filter"] = JSON.stringify({
        ...params.filter,
        [params.target]: params.id,
      });
    }

    const url = `${apiUrl}/${resource}?${stringify(query)}`;

    return httpClient(url, getUser()).then(({ headers, json }: any) => ({
      data: json.data,
      total: json.total,
    }));
  },

  update: (resource: any, params: any) => {
    if (params.data.tenant) {
      delete params.data.tenant;
    }
    return httpClient(`${apiUrl}/${resource}/${params.id}`, {
      method: "PUT",
      body: JSON.stringify(params.data),
      ...getUser(),
    })
      .then(({ json }: any) => {
        if (json) {
          return "data" in json ? json : { data: json };
        }
        return {
          data: {
            id: params.id,
          },
          payload: params.data,
        };
      })
      .catch((error: any) => {
        const body = get(error, "body", {});
        const msg = composeError(body);
        return Promise.reject({ message: msg });
      });
  },

  updateMany: (resource: any, params: any) => {
    const query = {
      filter: JSON.stringify({ id: params.ids }),
    };
    return httpClient(`${apiUrl}/${resource}?${stringify(query)}`, {
      method: "PUT",
      body: JSON.stringify(params.data),
      ...getUser(),
    }).then(({ json }: any) => ({ data: json }));
  },

  customUpdateMany: (resource: any, params: any) => {
    return httpClient(`${apiUrl}/${resource}`, {
      method: "PUT",
      body: JSON.stringify(params.data),
      ...getUser(),
    }).then(({ json }: any) => ({ data: json }));
  },

  create: (resource: any, params: any) => {
    if (process.env.REACT_APP_CMS_TYPE === "TENANT") {
      params.data = {
        ...params.data,
        tenantId: process.env.REACT_APP_TENANT_ID,
      };
    }
    return httpClient(`${apiUrl}/${resource}`, {
      method: "POST",
      body: JSON.stringify(params.data),
      ...getUser(),
    })
      .then(({ json }: any) => {
        return {
          data: { ...params.data, id: json.id },
          res: { json },
        };
      })
      .catch((error: any) => {
        const body = get(error, "body", {});
        const msg = composeError(body);
        return Promise.reject({ message: msg });
      });
  },

  delete: (resource: any, params: any) =>
    httpClient(`${apiUrl}/${resource}/${params.id}`, {
      method: "DELETE",
      ...getUser(),
    }).then(({ json }: any) => {
      const data: any = { id: params.id };

      return { data };
    }),

  deleteMany: (resource: any, params: any) => {
    const ids = params.ids;
    const deleteResource = (id: number) =>
      httpClient(`${apiUrl}/${resource}/${id}`, {
        method: "DELETE",
        ...getUser(),
      })
        .then(({ body }: any) => JSON.parse(body))
        .then(({ message }: any) => (message === "Success" ? id : null));

    return Promise.all(ids.map(deleteResource)).then((ids: any) => {
      return {
        data: ids,
      };
    });
  },

  uploadImage: (file: any, progressCallback: (percent: any) => {}) => {
    let formData = new FormData();

    formData.append("file", file);

    return axios
      .request({
        method: "post",
        url: `${apiUrl}/Medias/upload`,
        data: formData,
        onUploadProgress: (progressEvent) => {
          const percentage = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          progressCallback(percentage);
        },
      })
      .then((data) => {
        return {
          data: {
            url: `${apiHost}/${data.data.relativePath}`,
          },
        };
      });
  },

  customUpdate: (resource: any, params: any) => {
    return httpClient(`${apiUrl}/${resource}`, {
      method: "PUT",
      body: JSON.stringify(params.data),
      ...getUser(),
    })
      .then(({ json }: any) => {
        if (json) {
          return "data" in json ? json : { data: json };
        }
        return {
          data: {
            id: params.id,
          },
          payload: params.data,
        };
      })
      .catch((error: any) => {
        const body = get(error, "body", {});
        const msg = composeError(body);
        return Promise.reject({ message: msg });
      });
  },

  customCreate: (resource: any, params: any) => {
    return httpClient(`${apiUrl}/${resource}`, {
      method: "POST",
      body: JSON.stringify(params.data),
      ...getUser(),
    })
      .then(({ status }: any) => {
        return {
          data: { ...params.data },
        };
      })
      .catch((error: any) => {
        const body = get(error, "body", {});
        const msg = composeError(body);
        return Promise.reject({ message: msg });
      });
  },

  customPatch: (resource: any, params: any) => {
    return httpClient(`${apiUrl}/${resource}`, {
      method: "PATCH",
      body: JSON.stringify(params.data),
      ...getUser(),
    })
      .then(({ status }: any) => {
        if (status === 204) {
          return {
            data: { ...params.data },
          };
        }
      })
      .catch((error: any) => {
        const body = get(error, "body", {});
        const msg = composeError(body);
        return Promise.reject({ message: msg });
      });
  },

  customGetList: (resource: any, params: any) => {
    let query: Query = {};

    if (has(params, "filter")) {
      if (params.filter.search) {
        query["Search"] = params.filter.search ?? "";
        if(resource !== "parents"){
          query["filter"] = JSON.stringify(params.filter);
        }

        query["filter"] = JSON.stringify(params.filter);
      } else {
        query["filter"] = JSON.stringify(params.filter);
      }
    }

    let url = `${apiUrl}/${resource}?${stringify(query)}`;

    return httpClient(url, getUser()).then(({ json }: any) => ({
      data: json,
    }));
  },

  customGetOne: async (resource: any) => {
    console.log("RESOURCE_CUSTOM_GET_ONE", resource);
    const { json } = await httpClient(`${apiUrl}/${resource}`, getUser());

    return "data" in json ? json : { data: json };
  },

  customGetListWithStaticParams: (resource: any) => {
    let url = `${apiUrl}/${resource}`;

    return httpClient(url, getUser()).then(({ json }: any) => ({
      data: json,
    }));
  },

  customDelete: (resource: any, params: any) => {
    return httpClient(`${apiUrl}/${resource}`, {
      method: "DELETE",
      ...getUser(),
    }).then(({ json }: any) => {
      const data: any = { id: params.id };

      return { data };
    });
  },

  getOneAxios: (resource: any, params: any) => {
    return axios
      .request({
        method: "get",
        url: `${apiUrl}/${resource}/${params.id}`,
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        data: {},
      })
      .then((data) => {
        return { data: data.data };
      });
  },
};

export const getUser = () => {
  return {
    user: {
      authenticated: true,
      token: `Bearer ${localStorage.getItem("token")}`,
    },
  };
};

export const httpClient = fetchUtils.fetchJson;
export const apiHost = process.env.REACT_APP_API_HOST;
export const apiUrl = `${apiHost}/api`;

export default dataProvider;
